import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, getAuthToken, setAuthToken } from "../lib/api";
import { shutdownIntercom } from "../lib/chat";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const { pathname } = useLocation();
  const [user, setUser] = useState(null); // null = checking, false = guest, obj = user

  const checkSession = useCallback(() => {
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        setAuthToken(null);
        setUser(false);
      });
  }, []);

  useEffect(() => {
    if (!pathname.startsWith("/admin")) {
      setUser(false);
      return;
    }

    if (!getAuthToken()) {
      setUser(false);
      return;
    }

    checkSession();
  }, [checkSession, pathname]);

  const login = useCallback(async (identifier, password) => {
    try {
      const { data } = await api.post("/auth/login", { identifier, password });
      setAuthToken(data.token);
      setUser({ id: data.id, email: data.email, username: data.username, name: data.name, role: data.role });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      toast.message("Signed out locally");
    }
    shutdownIntercom();
    setAuthToken(null);
    setUser(false);
  }, []);

  const value = useMemo(() => ({ user, login, logout }), [user, login, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
